import has from '@atomicservice/ascf-web-sdk';
import { isHarmony } from './env';

// ASCF（鸿蒙元服务）web-view 不把 navigator.geolocation 桥接到系统定位，
// 浏览器原生定位在里面永远拿不到坐标。而 MapKit JS 的 showsUserLocationControl
// 所以这里在 ASCF 环境下直接把 navigator.geolocation 替换成走 Harmony SDK实现，
// ASCF 运行时会向 H5 注入 ascfwebProxy 桥（SDK 底层靠它与原生通信）。
// UA 判断可能因运行时版本差异不成立，桥的存在是更可靠的信号。
const isAscfRuntime = () =>
  isHarmony ||
  typeof (window as { ascfwebProxy?: unknown }).ascfwebProxy !== 'undefined';

// has.getLocation 是一次性接口，没有持续定位能力，watchPosition 用轮询模拟
const WATCH_INTERVAL = 5000;

// GeolocationPositionError.POSITION_UNAVAILABLE
const POSITION_UNAVAILABLE = 2;

type AscfLocation = {
  latitude: number;
  longitude: number;
  altitude?: number;
  accuracy?: number;
  speed?: number;
};

const toPosition = (res: AscfLocation): GeolocationPosition =>
  ({
    coords: {
      latitude: res.latitude,
      longitude: res.longitude,
      altitude: res.altitude ?? null,
      accuracy: res.accuracy ?? 0,
      altitudeAccuracy: null,
      heading: null,
      speed: res.speed ?? null
    },
    timestamp: Date.now()
  }) as GeolocationPosition;

const toPositionError = (error: unknown): GeolocationPositionError => {
  const { code, message } = (error || {}) as {
    code?: number;
    message?: string;
  };
  return {
    code: POSITION_UNAVAILABLE,
    message: `ascf getLocation failed: ${message ?? 'unknown'}${
      code === undefined ? '' : ` (code ${code})`
    }`,
    PERMISSION_DENIED: 1,
    POSITION_UNAVAILABLE: 2,
    TIMEOUT: 3
  } as GeolocationPositionError;
};

const requestLocation = (
  onSuccess: PositionCallback,
  onError?: PositionErrorCallback | null
) => {
  has.getLocation({
    type: 'wgs84',
    success: res => {
      if (res) {
        onSuccess(toPosition(res as AscfLocation));
      }
    },
    fail: error => {
      console.error(
        '[ascfGeolocation] has.getLocation fail:',
        JSON.stringify(error)
      );
      onError?.(toPositionError(error));
    }
  });
};

export const installAscfGeolocation = (): void => {
  if (!isAscfRuntime()) {
    return;
  }

  const geolocation: Geolocation = {
    getCurrentPosition: (onSuccess, onError) =>
      requestLocation(onSuccess, onError),
    watchPosition: (onSuccess, onError) => {
      requestLocation(onSuccess, onError);
      return window.setInterval(
        () => requestLocation(onSuccess, onError),
        WATCH_INTERVAL
      );
    },
    clearWatch: watchId => window.clearInterval(watchId)
  };

  try {
    Object.defineProperty(navigator, 'geolocation', {
      value: geolocation,
      configurable: true,
      writable: false
    });
    console.info(
      '[ascfGeolocation] navigator.geolocation 已替换为 ASCF 桥实现'
    );
  } catch (error) {
    console.error('[ascfGeolocation] 替换 navigator.geolocation 失败:', error);
  }
};
